import { IconArrowLeft, IconDownload, IconMaximize, IconCopy } from '@tabler/icons-react';

interface SendModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableBalance: number;
  onSend: (data: {
    address: string;
    network: string;
    amount: number;
  }) => void;
}

export default function SendModal({
  isOpen,
  onClose,
  availableBalance,
  onSend,
}: SendModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-0 md:p-4">
      <div className="relative w-full h-full md:h-auto md:max-h-[85vh] md:w-[480px] bg-background rounded-none md:rounded-xl overflow-hidden shadow-lg ring-1 ring-surface/5">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 md:px-6 py-4 border-b border-surface bg-background/80 backdrop-blur-sm">
          <button
            type="button"
            onClick={onClose}
            className="p-2 -ml-2 text-text-secondary hover:text-white rounded-lg transition-colors"
            aria-label="Go back"
          >
            <IconArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-semibold text-white">Send USDC</h1>
          <div className="w-10" /> {/* Spacer for alignment */}
        </div>

        {/* Main Content */}
        <div className="flex flex-col h-[calc(100%-144px)] md:h-auto">
          <form 
            className="flex-1 overflow-y-auto" 
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="flex flex-col items-center text-center px-4 md:px-6 py-6 space-y-6">
              {/* Address Input */}
              <div className="w-full max-w-md space-y-3">
                <div className="flex justify-between items-center">
                  <label htmlFor="address" className="text-text-secondary text-sm font-medium">Address</label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="p-2 text-text-secondary hover:text-white rounded-md transition-colors"
                      aria-label="Download"
                    >
                      <IconDownload size={20} />
                    </button>
                    <button
                      type="button"
                      className="p-2 text-text-secondary hover:text-white rounded-md transition-colors"
                      aria-label="Maximize"
                    >
                      <IconMaximize size={20} />
                    </button>
                  </div>
                </div>
                <div className="bg-surface/10 p-6 rounded-2xl">
                  <input
                    id="address"
                    type="text"
                    placeholder="Long press to paste"
                    className="w-full bg-surface text-white rounded-lg p-4 border border-surface focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none transition-all"
                  />
                </div>
              </div>

              {/* Network Selection */}
              <div className="w-full max-w-md space-y-3">
                <div className="flex justify-between items-center">
                  <label htmlFor="network" className="text-text-secondary text-sm font-medium">Network</label>
                  <button
                    type="button"
                    className="text-text-secondary hover:text-white rounded-full transition-colors"
                    aria-label="Network information"
                  >
                    ⓘ
                  </button>
                </div>
                <button
                  id="network"
                  type="button"
                  className="w-full bg-surface text-white rounded-lg p-4 flex justify-between items-center border border-surface hover:border-primary/50 transition-colors"
                >
                  <span>Select Network</span>
                  <span className="text-text-secondary">▼</span>
                </button>
              </div>

              {/* Withdrawal Amount */}
              <div className="w-full max-w-md space-y-3">
                <div className="flex justify-between items-center">
                  <label htmlFor="amount" className="text-text-secondary text-sm font-medium">Withdrawal Amount</label>
                  <button
                    type="button"
                    className="text-text-secondary hover:text-white rounded-full transition-colors"
                    aria-label="Amount information"
                  >
                    ⓘ
                  </button>
                </div>
                <div className="relative">
                  <input
                    id="amount"
                    type="number"
                    placeholder="Minimum 0"
                    className="w-full bg-surface text-white rounded-lg p-4 pr-24 border border-surface focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none transition-all"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    <span className="text-white">USDC</span>
                    <button
                      type="button"
                      className="px-2 py-1 text-primary hover:text-primary/90 text-sm font-medium transition-colors"
                    >
                      Max
                    </button>
                  </div>
                </div>
              </div>

              {/* Available Balance */}
              <div className="w-full max-w-md flex justify-between items-center text-sm">
                <span className="text-text-secondary">Available</span>
                <span className="text-white">{availableBalance.toFixed(8)} USDC ▼</span>
              </div>

              {/* Warning Messages */}
              <div className="w-full max-w-md space-y-2 text-sm text-text-secondary">
                <p>* Do not withdraw directly to a crowdfund or ICO. We will not credit your account with tokens from that sale.</p>
                <p className="flex items-center gap-2">
                  * Do not transact with Sanctioned Entities.
                  <a href="/learn-more" className="text-primary hover:text-primary/90 transition-colors">Learn more</a>
                </p>
              </div>
            </div>
          </form>

          {/* Network Fee and Total - Fixed at bottom */}
          <div className="sticky bottom-0 left-0 right-0 p-4 md:p-6 bg-background/80 backdrop-blur-sm border-t border-surface">
            <div className="max-w-md mx-auto space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-secondary">Network fee</span>
                <span className="text-white">0.00 USDC</span>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-surface text-white font-medium px-6 md:px-8 py-3 rounded-lg hover:bg-surface/80 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-primary text-black font-medium px-6 md:px-8 py-3 rounded-lg hover:bg-primary/90 active:scale-[0.98] transition-all"
                >
                  Withdraw
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 